import { DatePipe } from '@angular/common';
import { Component, Inject, OnInit, Renderer2 } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import {
  MaxLength,
  ModuleName,
  Modules,
  NotificationTextMessage,
} from '@app/core/Enum';
import { CommonService } from '@app/core/Services';
import { AddClosePopupComponent } from '@app/modules';
import { Subscription } from 'rxjs';

import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { NgxSpinnerService } from 'ngx-spinner';
import { CustomEmail } from '@app/core/Store';
import { Store } from '@ngxs/store';

@Component({
  selector: 'app-email-popup',
  templateUrl: './email-popup.component.html',
  styleUrls: ['./email-popup.component.scss'],
})
export class EmailPopupComponent implements OnInit {
  emailForm: FormGroup;
  fb: FormBuilder = new FormBuilder();
  moduleName = '';
  moduleId = 0;
  subscriptionRouting: Subscription;
  emailArray: any;
  emailData: any;

  content = '';
  ids;

  maxLength = MaxLength;

  removable = true;
  public emailList = [];
  public ccList = [];
  public bccList = [];
  public separatorKeysCodes = [ENTER, COMMA];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialog: MatDialog,
    public commonService: CommonService,
    public datepipe: DatePipe,
    private spinner: NgxSpinnerService,
    private store: Store,
    public dialogRef: MatDialogRef<EmailPopupComponent>,
    private renderer: Renderer2
  ) {}

  ngOnInit(): void {
    this.setForm();
    this.ids = this.data.ids;

    switch (this.data.moduleId) {
      case Modules.Receipt:
        this.moduleId = Modules.Receipt;
        this.moduleName = ModuleName.Receipt;
        break;

      case Modules.Invoices:
        this.moduleId = Modules.Invoices;
        this.moduleName = ModuleName.Invoices;
        break;

      case Modules.Quotation:
        this.moduleId = Modules.Quotation;
        this.moduleName = ModuleName.Quotation;
        break;

      case Modules.CreditNote:
        this.moduleId = Modules.CreditNote;
        this.moduleName = ModuleName.CreditNote;
        break;

      case Modules.DebitNote:
        this.moduleId = Modules.DebitNote;
        this.moduleName = ModuleName.DebitNote;
        break;
    }
  }

  removeEmail(data: any, type): void {
    if (type === 1) {
      if (this.emailList.indexOf(data) >= 0) {
        this.emailList.splice(this.emailList.indexOf(data), 1);
      }
    } else if (type === 2) {
      if (this.ccList.indexOf(data) >= 0) {
        this.ccList.splice(this.ccList.indexOf(data), 1);
      }
    }
    if (this.bccList.indexOf(data) >= 0) {
      this.bccList.splice(this.bccList.indexOf(data), 1);
    }
  }

  add(event, type): void {
    if (!event.value) {
      this.resetInput(event);
      return;
    }

    const targetList = this.getTargetList(type);
    const isValid = this.validateEmail(event.value);

    targetList.push({ value: event.value, invalid: !isValid });

    this.resetInput(event);
  }

  private getTargetList(type: number): any[] {
    switch (type) {
      case 1:
        return this.emailList;
      case 2:
        return this.ccList;
      default:
        return this.bccList;
    }
  }

  private resetInput(event): void {
    if (event.input) {
      event.input.value = '';
    }
  }

  onCloseClick(): void {
    this.dialog.open(AddClosePopupComponent, {});
  }

  private validateArrayNotEmpty(c: FormControl) {
    if (c.value && c.value.length === 0) {
      return {
        validateArrayNotEmpty: { valid: false },
      };
    }
    return null;
  }

  private validateEmail(email) {
    let re =
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  }

  setForm(): void {
    this.emailForm = this.fb.group({
      to: this.fb.array(
        [],
        new FormControl([
          Validators.required,
          this.commonService.whiteSpaceValidate,
        ])
      ),
      cc: this.fb.array([], new FormControl()),
      bcc: this.fb.array([], new FormControl()),
      subject: new FormControl(null, Validators.required),
      content: new FormControl(),
    });
  }

  dataSubmit(): any {
    const totalCount = this.emailList?.length;
    if (totalCount > 10) {
      this.commonService.onFailure(
        NotificationTextMessage.emailRecipientLimitExceeded
      );
      return false;
    }
    if (
      this.emailForm.controls.content.value === null ||
      this.emailForm.controls.content.value === undefined ||
      this.emailForm.controls.content.value === ''
    ) {
      this.commonService.onFailure(NotificationTextMessage.emptyBody);
      return false;
    }

    if (
      this.ccList.filter((x: any) =>
        this.emailList.some((y: any) => y.value === x.value)
      ).length !== 0 ||
      this.bccList.filter((x: any) =>
        this.emailList.some((y: any) => y.value === x.value)
      ).length !== 0 ||
      this.ccList.filter((x: any) =>
        this.bccList.some((y: any) => y.value === x.value)
      ).length !== 0
    ) {
      this.commonService.onFailure(NotificationTextMessage.duplicateMail);
      return false;
    }

    if (
      this.emailList.filter((x: any) => x.invalid === true).length === 0 &&
      this.ccList.filter((x: any) => x.invalid === true).length === 0 &&
      this.bccList.filter((x: any) => x.invalid === true).length === 0
    ) {
      this.spinner.show();
      const toEmailList: any[] = [];
      const ccEmailList: any[] = [];
      const bccEmailList: any[] = [];
      this.emailList.forEach((element: any) => {
        toEmailList.push(element.value);
      });
      this.ccList.forEach((element: any) => {
        ccEmailList.push(element.value);
      });
      this.bccList.forEach((element: any) => {
        bccEmailList.push(element.value);
      });

      try {
        const emailObj: any = {
          toEmail: toEmailList,
          ccEmail: ccEmailList,
          bccEmail: bccEmailList,
          subject: this.emailForm.controls.subject.value,
          message: this.emailForm.controls.content.value,
          ids: this.ids,
        };

        this.emailData = <JSON>emailObj;
        return true;
      } catch (error) {
        this.spinner.hide();
        this.commonService.onFailure(NotificationTextMessage.errorMessage);
        return false;
      }
    } else {
      this.commonService.onFailure(NotificationTextMessage.errorMessage);
      return false;
    }
  }

  onSave(): void {
    if (this.emailForm.invalid) {
      this.commonService.addValidation(this.emailForm, this.renderer);
    } else {
      if (this.dataSubmit()) {
        this.store
          .dispatch(new CustomEmail(this.moduleId, this.emailData))
          .pipe()
          .subscribe(
            (res) => {
              const resData = res?.common?.emailData;
              if (resData?.status) {
                this.onCancel(true);
                this.commonService.onSucess(
                  NotificationTextMessage.mailSentSuccessfully
                );
              } else {
                this.commonService.onFailure(
                  resData?.message
                );
              }
            },
            (err) => {
              this.commonService.onFailure(err.error.Message);
            }
          );
      }
    }
  }

  onCancel(res: boolean): void {
    this.dialogRef.close(res);
  }
}
